import type { CampaignQaRuleResult } from "@dashboardApi/apis/generativeAiAppGroupIdAgentPreview/types";
import type { CampaignQaAgentResult } from "@dashboardApi/apis/generativeAiAppGroupIdCampaignQa/types";

import type { CampaignQaPusherChunk } from "./types";

/**
 * Multi-agent campaign QA: reassemble per-agent results from ordered chunks.
 *
 * Each chunk carries one ruleset slice for a single agent. Chunks arrive in
 * arbitrary order, so we sort by chunk_index first, then group by agent_id.
 * If any chunk for an agent carries an error, that error wins and the agent's
 * qa_results are discarded (the backend only sends error chunks when the
 * entire agent execution failed).
 */
export function mergeCampaignQaPusherChunks(chunks: CampaignQaPusherChunk[]): CampaignQaAgentResult[] {
  const sorted = [...chunks].sort((a, b) => a.chunk_index - b.chunk_index);
  const byAgentId = new Map<string, CampaignQaAgentResult>();

  for (const c of sorted) {
    // Error chunk: overwrites any previously-accumulated qa_results for this agent
    if (c.error_code) {
      byAgentId.set(c.agent_id, {
        agent_id: c.agent_id,
        error_code: c.error_code,
        error_message: c.error_message,
      });
      continue;
    }

    // Skip qa_results chunks that arrive after an error for the same agent
    const prev = byAgentId.get(c.agent_id);
    if (prev?.error_code) {
      continue;
    }

    const qaPatch = c.qa_results ?? [];
    if (!prev) {
      byAgentId.set(c.agent_id, { agent_id: c.agent_id, qa_results: [...qaPatch] });
    } else {
      byAgentId.set(c.agent_id, {
        agent_id: c.agent_id,
        qa_results: [...(prev.qa_results ?? []), ...qaPatch],
      });
    }
  }

  return Array.from(byAgentId.values());
}

/**
 * Single-agent campaign QA preview: flatten all ruleset slices into one
 * ordered qa_results array. Error chunks are skipped (the preview flow
 * surfaces errors via the completion envelope's error_code instead).
 */
export function mergeCampaignQaPreviewChunks(chunks: CampaignQaPusherChunk[]): CampaignQaRuleResult[] {
  const sorted = [...chunks].sort((a, b) => a.chunk_index - b.chunk_index);
  const out: CampaignQaRuleResult[] = [];
  for (const c of sorted) {
    if (c.error_code) {
      continue;
    }
    out.push(...(c.qa_results ?? []));
  }
  return out;
}
